<template>
  <EditorDeckThreeDView
    v-model:active-tool="activeTool"
    :edit-mode="editMode"
    :loading="loading"
    :is-composing-new-scene="isComposingNewScene"
    :is-saving-as-scene="isSavingAsScene"
    :is-duplicating-scene="isDuplicatingScene"
    :is-editing-individual-scene="true"
    :three-d-viewer-error="threeDViewerError"
    :three-d-viewer="threeDViewer"
    :context-menu="contextMenu"
    :slide-title="slideTitle"
    :show-slide-title="showSlideTitle"
    :company-id="deck && deck.company.id"
    :progress="progress"
    @update:slide-title="$emit('update:slide-title', { id: slide.id, title: $event.title })"
    @update:show-slide-title="toggleTitleVisibility"
    @save="save"
    @save-as="saveAs"
    @update-text-scale="$emit('update-text-scale', $event)"
    @back="$emit('leave-edit-scene')"
    @cancel="$emit('leave-edit-scene')"
    @contextmenu="$emit('contextmenu', $event)"
    @change="$emit('change')"
    @move-flagpole="trackFlagpoleMovement"
    @reposition-flagpole="trackFlagpolePositioning"
    @change-model-drill-data-localization="$emit('change-model-drill-data-localization', $event)"
    @remove-drill-data-from-model="$emit('remove-drill-data-from-model', $event)"
    @notify="$emit('notify', $event)"
  >
    <template #top-bar>
      <EditorTitleOverlay v-if="editMode === 'read'" class="absolute top-0 left-0 right-0">
        <div class="flex flex-col w-full">
          <div class="flex items-center">
            <div class="flex flex-col items-center justify-center flex-grow drop-shadow max-w-3/4">
              <div class="flex items-start justify-center text-xl text-gray-200 cursor-pointer slide-title max-w-4/5">
                <span role="button" :title="showSlideTitle ? 'Title is visible when presenting' : 'Title is hidden when presenting'">
                  <EyeIcon v-if="showSlideTitle" class="w-5 h-5 mx-2 mt-2 -top-px" @click="toggleTitleVisibility()" />
                  <EyeSlashIcon v-else class="w-5 h-5 mx-2 mt-2 -top-px" @click="toggleTitleVisibility()" />
                </span>
                <InlineEditable
                  ref="inlineEditableRefWrapper"
                  v-model:value="slideTitle"
                  :maxlength="255"
                  class="max-w-full whitespace-nowrap"
                  theme="dark"
                  placeholder="Slide title"
                  @save="updateTitle"
                  @cancel="resetTitle"
                  @clear="resetTitle"
                  @blur="blurTitle"
                />
              </div>
            </div>

            <div class="flex flex-none ml-auto gap-2">
              <VButton size="xs" :disabled="progress < 1" data-intercom-target="edit-3d-scene" @click="$emit('update:editMode', 'write')">
                <CubeTransparentIcon class="inline-block w-4 h-4" /> Edit Slide
              </VButton>
            </div>
          </div>

          <VMessage v-if="true" class="mt-3.5 items-center" :show="showWarning" banner-type="private"
            >Looks like this slide is using private drill data and will not be visible in public decks
            <template #dismiss>
              <VButton class="w-full border border-gray-300" size="sm" no-shadow="noShadow" no-border="noBorder" @click="turnDataPublic"
                >Turn data public</VButton
              >
            </template>
          </VMessage>
        </div>
      </EditorTitleOverlay>
    </template>

    <Portal to="deck-editor-header-controls">
      <div class="flex gap-x-2 items-baseline row-start-2 xl1_row-start-1">
        <VButton
          class="header-button bg-copper-400 min-w-12"
          data-testid="select-btn"
          :class="{ active: activeTool === 'select' }"
          variant="copper-borderless"
          no-shadow
          @click="activeTool = 'select'"
        >
          <FontAwesomeIcon icon="arrow-pointer" size="lg" class="transform scale-125" />
          <span>Select</span>
        </VButton>

        <VTooltip direction="top">
          <template #content>
            <div class="whitespace-nowrap">
              Text Labels
              <span class="text-gray-400">({{ modifierKey }} + L)</span>
            </div>
          </template>
          <VButton
            class="header-button bg-copper-400 min-w-12"
            data-testid="text-label-btn"
            :class="{ active: activeTool === 'label-creation' }"
            :disabled="loading"
            variant="copper-borderless"
            no-shadow
            @click="activeTool = 'label-creation'"
          >
            <TypeIcon class="w-4" />
            <span>Label</span>
          </VButton>
        </VTooltip>

        <VPopup v-model="showDrillDataPopup">
          <template #trigger>
            <VButton
              v-if="userState.flags.MANAGE_DRILL_LABELS_ENABLED"
              class="header-button bg-copper-400"
              data-testid="select-btn"
              :class="{ active: showDrillDataPopup }"
              variant="copper-borderless"
              no-shadow
              @click="showDrillDataPopup = !showDrillDataPopup"
            >
              <div class="flex">
                <CircleStackIcon class="w-5" />
                <ChevronDownIcon v-if="showDrillDataPopup" class="flex-shrink-0 w-3 -mt-0.5 ml-1" />
              </div>
              <span>Drill Data</span>
            </VButton>
          </template>

          <template #content>
            <EditorDrillDataList
              type="drill"
              :three-d-viewer="threeDViewer"
              :deck="deck"
              @add-to-model="addToModel"
              @selected="$emit('select-drill-data', $event)"
            />
          </template>
        </VPopup>

        <VPopup v-model="showAiImportPopup" content-class="max-w-xl">
          <template #trigger>
            <VButton
              v-if="userState.flags.MANAGE_AI_TARGET_IMPORTS_ENABLED"
              class="header-button bg-copper-400"
              data-testid="select-btn"
              :class="{ active: showAiImportPopup }"
              variant="copper-borderless"
              no-shadow
              @click="showAiImportPopup = !showAiImportPopup"
            >
              <div class="flex">
                <PhotoIcon class="w-6" />
                <ChevronDownIcon v-if="showAiImportPopup" class="flex-shrink-0 w-3 -mt-0.5 ml-1" />
              </div>
              <span>Import AI Targets</span>
            </VButton>
          </template>

          <template #content>
            <EditorDrillDataList
              type="ai_target"
              :three-d-viewer="threeDViewer"
              :deck="deck"
              @add-to-model="addToModel"
              @selected="$emit('select-drill-data', $event)"
            />
          </template>
        </VPopup>
      </div>
    </Portal>
  </EditorDeckThreeDView>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { computed, watch, ref, toRefs } from 'vue'
import { Portal } from 'portal-vue'
import { CubeTransparentIcon } from '@heroicons/vue/24/outline'
import { EyeIcon, EyeSlashIcon, CircleStackIcon, ChevronDownIcon, PhotoIcon } from '@heroicons/vue/24/solid'

import type { Slide } from '~/entities/deck'
import type { LabelToolTypes } from '~/lib/three-d-viewer/tools/ToolManager'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { DrillDataSet } from '~/entities/drillLabel'
import type { ThreeDEditMode } from '~/entities/model3d'
import type { EditDeck } from '~/composables/useEditDeck'
import TypeIcon from '~/components/icons/TypeIcon.svg'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    default: null,
  },
  uuid: {
    type: String as PropType<string | null>,
    default: null,
  },
  editMode: {
    type: String as PropType<ThreeDEditMode>,
    required: true,
  },
  isComposingNewScene: {
    type: Boolean,
    required: true,
  },
  isSavingAsScene: {
    type: Boolean,
    required: true,
  },
  isDuplicatingScene: {
    type: Boolean,
    required: true,
  },
  newlyCreatedSlideId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  contextMenu: {
    type: Object as PropType<{ show: boolean; context: any }>,
    required: true,
  },
  threeDViewerError: {
    type: String,
    default: '',
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const emit = defineEmits([
  'add-drill-data-to-model',
  'add-drill-data-v1-model',
  'change-model-drill-data-localization',
  'blur-title',
  'change',
  'contextmenu',
  'leave-edit-scene',
  'notify',
  'remove-drill-data-from-model',
  'save',
  'save-as',
  'select-drill-data',
  'update-text-scale',
  'update:editMode',
  'update:slide',
  'update:slide-title',
  'turn-data-public',
])

const { state: userState } = useUser()
const { $analytics } = useNuxtApp()
const slideTitle = ref(props.slide.title)
const showSlideTitle = ref(props.slide.title_visible_in_3d_model ?? false)
const activeTool = ref<LabelToolTypes>('none')
const inlineEditableRefWrapper = ref<any | undefined>()
const showWarning = ref(false)
const drillLabels = ref<DrillDataSet[]>([])
const { slide, newlyCreatedSlideId } = toRefs(props)
const showDrillDataPopup = ref(false)
const showAiImportPopup = ref(false)

const progress = computed(() => (props.threeDViewer.progress.value + props.threeDViewer.hiddenLayerProgress.value) / 2)

const { modifierKey } = useKeyboardShortcut({
  disabled: computed(() => props.editMode !== 'write'),
  opts: {
    l: () => (activeTool.value = 'label-creation'),
  },
})

watch(
  [slide, newlyCreatedSlideId, inlineEditableRefWrapper],
  ([slide, newlyCreatedSlideId]) => {
    if (slide.id === newlyCreatedSlideId && inlineEditableRefWrapper.value?.inputElement) {
      inlineEditableRefWrapper.value?.inputElement.value?.click?.() // focus title on a newlyCreatedSlide
      emit('update:editMode', 'write')
    }
  },
  { flush: 'post' },
)

watch(
  slide,
  async newSlide => {
    slideTitle.value = newSlide.title
    showSlideTitle.value = newSlide.title_visible_in_3d_model ?? false

    if (!newSlide.model_id || !props.deck) return

    drillLabels.value =
      (await props.threeDViewer.world.drillDataSetManager?.getDataSets({
        modelId: newSlide.model_id,
        companyId: props.deck.company_id,
      })) || []

    showWarning.value =
      props.deck.view_access_type === 'public' &&
      drillLabels.value.some(
        (drill: DrillDataSet) => drill.data_set.view_access_type === 'private' || drill.data_set.view_access_type === 'secure',
      )
  },
  { immediate: true },
)

function save(title: string) {
  emit('save', props.slide, title)

  $analytics.track('DeckEditor3DSceneEditorClickSave', {
    deckName: props.deck?.title,
    deckId: props.deck?.id,
    modelId: props.slide?.model_id,
  })
}

function saveAs(title: string) {
  emit('save-as', props.slide, title)

  $analytics.track('DeckEditor3DSceneEditorClickSaveAs', {
    deckName: props.deck?.title,
    deckId: props.deck?.id,
    modelId: props.slide?.model_id,
  })
}

function addToModel() {
  showDrillDataPopup.value = false
  showAiImportPopup.value = false

  const model = props.threeDViewer.world.currentModel

  if (model && model.apiData.version < 2) {
    emit('add-drill-data-v1-model')
    return
  }

  emit('add-drill-data-to-model')
}

function trackFlagpoleMovement(labelId: number) {
  $analytics.track('DeckEditor3DSlideLabelClickMove', {
    deckId: props.deck?.id,
    deckName: props.deck?.title,
    companyName: props.deck?.company?.name,
    companyId: props.deck?.company_id,
    labelId,
  })
}

function trackFlagpolePositioning(labelId: number) {
  $analytics.track('DeckEditor3DSlideLabelClickReposition', {
    deckId: props.deck?.id,
    deckName: props.deck?.title,
    companyName: props.deck?.company?.name,
    companyId: props.deck?.company_id,
    labelId,
  })
}

async function turnDataPublic() {
  showWarning.value = false
  const privateDataSets = drillLabels.value.filter(
    (drill: DrillDataSet) => drill.data_set.view_access_type === 'private' || drill.data_set.view_access_type === 'secure',
  )

  await props.threeDViewer.world.drillDataSetManager?.updateDataSets({
    companyId: props.deck!.company_id,
    drillDataSets: privateDataSets,
    drillDataSetUpdates: { view_access_type: 'public' },
  })
}

function resetTitle() {
  slideTitle.value = props.slide.title
}

async function updateTitle() {
  const title = slideTitle.value?.trim() ?? ''

  if (title === props.slide.title) return

  if (title === '') {
    slideTitle.value = props.slide.title
    return
  }

  emit('update:slide-title', { id: props.slide.id, title })
}

function blurTitle() {
  emit('blur-title')
}

async function toggleTitleVisibility() {
  showSlideTitle.value = !showSlideTitle.value
  emit('update:slide', { id: props.slide.id, title_visible_in_3d_model: showSlideTitle.value })
}
</script>

<style lang="stylus" scoped>
.slide-title
  &.hidden
    @apply text-gray-500

  &:hover
    @apply text-white

.header-button
  @apply relative flex flex-col justify-between items-center text-xs leading-5 font-normal whitespace-nowrap px-2 gap-1

  &.active
    @apply bg-copper-400 text-white
    svg
      @apply text-white
</style>
